import { supabase } from './supabase';

/**
 * Helper Web Push : enregistrer / désinscrire / status d'un device.
 *
 * Le service worker (src/sw.ts) reçoit le push et affiche la notif. Ce module
 * gère uniquement le cycle de vie de la subscription côté client + persistance
 * en base via `push_subscriptions` (RLS : user voit/écrit ses propres lignes).
 *
 * Conformité CLAUDE.md :
 *  - Pure functions où possible (`urlBase64ToUint8Array`, `arrayBufferToBase64`)
 *  - Side effects isolés derrière 3 entry points (subscribe / unsubscribe / status)
 *  - Pas de `any`, types stricts sur les payloads Supabase
 *  - Aucun secret côté client : la VAPID_PUBLIC_KEY est exposée volontairement (clé publique)
 */

export type PushStatus =
  | { kind: 'unsupported' }
  | { kind: 'not-registered' }
  | { kind: 'denied' }
  | { kind: 'subscribed'; endpoint: string };

/**
 * Récupère la clé publique VAPID depuis app_settings via RPC, avec fallback sur
 * l'env var (rétrocompatibilité). La clé est cachée en mémoire pour la session.
 */
let _vapidPublicKey: string | null = null;
async function getVapidPublicKey(): Promise<string> {
  if (_vapidPublicKey) return _vapidPublicKey;

  // 1. Source primaire : app_settings via RPC
  try {
    const { data, error } = await supabase.rpc('get_vapid_public_key');
    if (!error && typeof data === 'string' && data.length > 0) {
      _vapidPublicKey = data;
      return data;
    }
  } catch {
    // ignore, fallback env var
  }

  // 2. Fallback build-time
  const envKey = (import.meta.env.VITE_VAPID_PUBLIC_KEY as string | undefined) ?? '';
  if (envKey) _vapidPublicKey = envKey;
  return envKey;
}

export function isPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export async function getStatus(): Promise<PushStatus> {
  if (!isPushSupported()) return { kind: 'unsupported' };
  if (Notification.permission === 'denied') return { kind: 'denied' };

  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return { kind: 'not-registered' };

  const sub = await reg.pushManager.getSubscription();
  if (!sub) return { kind: 'not-registered' };

  return { kind: 'subscribed', endpoint: sub.endpoint };
}

/**
 * Demande la permission + crée la subscription + persiste en base.
 * Idempotent : si déjà abonné, no-op.
 */
export async function subscribe(): Promise<PushStatus> {
  if (!isPushSupported()) return { kind: 'unsupported' };

  const vapidPublicKey = await getVapidPublicKey();
  if (!vapidPublicKey) {
    throw new Error('VAPID public key manquante — configure-la dans Admin > Settings > Paramètres push');
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return permission === 'denied' ? { kind: 'denied' } : { kind: 'not-registered' };
  }

  // `serviceWorker.ready` ne résout JAMAIS si aucun SW ne s'enregistre (dev,
  // enregistrement échoué) : sans timeout, le bouton « Activer » semblait mort.
  const reg = await Promise.race([
    navigator.serviceWorker.ready,
    new Promise<never>((_, reject) =>
      setTimeout(() => reject(new Error('Service worker indisponible — rechargez la page et réessayez')), 8000),
    ),
  ]);
  const existing = await reg.pushManager.getSubscription();
  if (existing) {
    await persistSubscription(existing);
    return { kind: 'subscribed', endpoint: existing.endpoint };
  }

  const doSubscribe = (): Promise<PushSubscription> =>
    reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
    });

  let sub: PushSubscription;
  try {
    sub = await doSubscribe();
  } catch (first) {
    // « push service error » est souvent TRANSITOIRE : juste après un déploiement,
    // le nouveau service worker s'installe (précache + reload) et le navigateur
    // refuse l'abonnement pendant cette fenêtre. Un unique retry après 3 s suffit.
    if (!isPushServiceError(first)) throw new Error(explainSubscribeError(first));
    await new Promise((r) => setTimeout(r, 3000));
    try {
      sub = await doSubscribe();
    } catch (second) {
      throw new Error(explainSubscribeError(second));
    }
  }
  await persistSubscription(sub);
  return { kind: 'subscribed', endpoint: sub.endpoint };
}

function isPushServiceError(err: unknown): boolean {
  const raw = err instanceof Error ? err.message : String(err);
  return /push service error|registration failed|abort/i.test(raw);
}

/**
 * Traduit les erreurs de `pushManager.subscribe()` en message actionnable.
 * « Registration failed - push service error » = le NAVIGATEUR n'a pas pu
 * joindre son service de push (FCM) : transitoire pendant une mise à jour du
 * SW, Brave sans les services Google de push, ou un proxy/VPN qui bloque
 * fcm.googleapis.com. Rien à corriger côté AutoQuote.
 */
function explainSubscribeError(err: unknown): string {
  const raw = err instanceof Error ? err.message : String(err);
  if (isPushServiceError(err)) {
    // Brave expose `navigator.brave` — son réglage dédié est LA cause classique.
    const isBrave = typeof navigator !== 'undefined' && 'brave' in navigator;
    const hint = isBrave
      ? 'Sur Brave : activez « Utiliser les services Google pour la messagerie push » dans brave://settings/privacy puis relancez le navigateur.'
      : 'Rechargez la page et réessayez dans quelques secondes (fréquent juste après une mise à jour de l’app). '
        + 'Si ça persiste, vérifiez qu’un VPN, proxy ou pare-feu ne bloque pas fcm.googleapis.com.';
    return `Votre navigateur n’a pas pu joindre son service de notifications push. ${hint} (détail : ${raw})`;
  }
  if (/permission/i.test(raw)) {
    return 'Permission de notification refusée par le navigateur — autorisez les notifications pour ce site puis réessayez.';
  }
  return raw;
}

export async function unsubscribe(): Promise<void> {
  if (!isPushSupported()) return;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return;

  const sub = await reg.pushManager.getSubscription();
  if (!sub) return;

  await sub.unsubscribe();
  await deletePersistedSubscription(sub.endpoint);
}

/* ── Persistance Supabase ─────────────────────────────────── */

async function persistSubscription(sub: PushSubscription): Promise<void> {
  const json = sub.toJSON();
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!p256dh || !auth) {
    throw new Error('PushSubscription keys missing — navigator support incomplete');
  }

  const { data: userRes, error: userErr } = await supabase.auth.getUser();
  if (userErr || !userRes.user) throw userErr ?? new Error('not authenticated');

  // upsert sur endpoint (UNIQUE constraint en DB)
  const { error } = await supabase.from('push_subscriptions').upsert(
    {
      user_id: userRes.user.id,
      endpoint: sub.endpoint,
      p256dh,
      auth,
      user_agent: navigator.userAgent,
      last_used_at: new Date().toISOString(),
    },
    { onConflict: 'endpoint' },
  );
  if (error) throw error;
}

async function deletePersistedSubscription(endpoint: string): Promise<void> {
  const { error } = await supabase.from('push_subscriptions').delete().eq('endpoint', endpoint);
  if (error) throw error;
}

/* ── Pure helpers (testables sans navigator) ──────────────── */

export function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  // Allocate over a concrete ArrayBuffer so the result satisfies BufferSource
  // (pushManager.subscribe rejects the wider Uint8Array<ArrayBufferLike>).
  const buffer = new ArrayBuffer(raw.length);
  const arr = new Uint8Array(buffer);
  for (let i = 0; i < raw.length; ++i) arr[i] = raw.charCodeAt(i);
  return arr;
}
